import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router";
import { Seletor } from "../componentes/conhecimento";
import {
  Aviso,
  Botao,
  Campo,
  Cartao,
  Celula,
  CelulaCabecalho,
  Chip,
  Cabecalho,
  Corpo,
  Linha,
  Tabela,
} from "../componentes/ui";
import {
  ORIGEM_ESCRITO_AQUI,
  ROTULO_DA_ORIGEM,
  ROTULO_DA_VISIBILIDADE,
  SEM_MODULO,
  ROTULO_DO_ESTADO_DO_ARTIGO,
  tomDoEstadoDoArtigo,
  useArtigosDoConsole,
  useCategoriasDeArtigo,
  useCriarCategoriaDeArtigo,
  useMetricasDaBase,
  useModulosDoCatalogo,
  useValorAdiado,
  type EstadoDoArtigo,
} from "../nucleo/conhecimento";
import { mensagemDoConsole } from "../nucleo/console";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";

function dataHora(valor: string): string {
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? "—"
    : new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(data);
}

function Metricas() {
  const metricas = useMetricasDaBase();
  if (metricas.isPending) return null;
  if (metricas.isError) return <Aviso tom="erro">{mensagemDoConsole(metricas.error)}</Aviso>;
  const dados = metricas.data;
  const avaliadas = dados.avaliacoes_uteis + dados.avaliacoes_nao_uteis;
  return (
    <Cartao titulo="Como a base está ajudando" className="mb-5" data-testid="metricas-da-base">
      <div className="grid gap-4 md:grid-cols-4">
        <div>
          <p className="nota-rodape">Artigos publicados</p>
          <strong data-testid="metrica-publicados">{dados.artigos_publicados}</strong>
        </div>
        <div>
          <p className="nota-rodape">Chamados evitados</p>
          <strong data-testid="metrica-desistencias">{dados.desistencias}</strong>
        </div>
        <div>
          <p className="nota-rodape">Taxa de deflexão</p>
          <strong data-testid="metrica-taxa">
            {dados.taxa_de_deflexao === null
              ? "sem dados ainda"
              : `${dados.taxa_de_deflexao.toLocaleString("pt-BR")}%`}
          </strong>
          <p className="nota-rodape">
            {dados.desistencias} desistiram · {dados.aberturas_apos_leitura} abriram mesmo assim
          </p>
        </div>
        <div>
          <p className="nota-rodape">Avaliações</p>
          <strong data-testid="metrica-avaliacoes">
            {avaliadas === 0
              ? "nenhuma ainda"
              : `${dados.avaliacoes_uteis} úteis · ${dados.avaliacoes_nao_uteis} não úteis`}
          </strong>
        </div>
      </div>
    </Cartao>
  );
}

function NovaCategoria() {
  const criar = useCriarCategoriaDeArtigo();
  const [nome, setNome] = useState("");

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    criar.mutate(nome.trim(), { onSuccess: () => setNome("") });
  }

  return (
    <form className="flex items-end gap-2" onSubmit={enviar}>
      <Campo
        rotulo="Nova categoria"
        value={nome}
        minLength={2}
        onChange={(evento) => setNome(evento.target.value)}
        classeDaMoldura="mb-0"
      />
      <Botao type="submit" variante="fantasma" disabled={nome.trim().length < 2 || criar.isPending}>
        Criar categoria
      </Botao>
      {criar.isError ? <Aviso tom="erro">{mensagemDoConsole(criar.error)}</Aviso> : null}
    </form>
  );
}

/** O console da base: lista, filtros, categorias e a taxa de deflexão. */
export function BaseDeConhecimento() {
  useTitulo("Base de conhecimento");
  const sessao = useSessao();
  const podeEditar = podeNaSessao(sessao.data, "conhecimento:editar");
  const [texto, setTexto] = useState("");
  const [estado, setEstado] = useState<EstadoDoArtigo | "">("");
  const [categoriaId, setCategoriaId] = useState("");
  const [origem, setOrigem] = useState("");
  const [modulo, setModulo] = useState("");
  const buscado = useValorAdiado(texto.trim(), 300);
  const categorias = useCategoriasDeArtigo(podeEditar);
  const modulos = useModulosDoCatalogo(podeEditar);
  const artigos = useArtigosDoConsole(
    { q: buscado, estado, categoria_id: categoriaId, origem, modulo },
    podeEditar,
  );

  if (!podeEditar) {
    return (
      <div>
        <h1 className="titulo-pagina">Base de conhecimento</h1>
        <Aviso tom="atencao">A sua conta não tem a permissão para editar a base de conhecimento.</Aviso>
      </div>
    );
  }

  return (
    <div data-testid="console-do-conhecimento">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="titulo-pagina">Base de conhecimento</h1>
          <p className="lead">
            Artigos que os clientes leem na Central de ajuda e que aparecem como sugestão antes de
            abrir um chamado.
          </p>
        </div>
        <Link className="botao" to="/admin/conhecimento/novo">
          Novo artigo
        </Link>
      </div>

      <Metricas />

      <Cartao titulo="Filtrar artigos" className="mb-5">
        <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-5">
          <Campo
            rotulo="Buscar artigo"
            type="search"
            value={texto}
            placeholder="Título ou palavras do texto"
            onChange={(evento) => setTexto(evento.target.value)}
          />
          <Seletor
            rotulo="Estado"
            valor={estado}
            aoMudar={(valor) => setEstado(valor as EstadoDoArtigo | "")}
          >
            <option value="">Todos</option>
            {Object.entries(ROTULO_DO_ESTADO_DO_ARTIGO).map(([valor, rotulo]) => (
              <option value={valor} key={valor}>
                {rotulo}
              </option>
            ))}
          </Seletor>
          <Seletor rotulo="Categoria" valor={categoriaId} aoMudar={setCategoriaId}>
            <option value="">Todas</option>
            {(categorias.data ?? []).map((categoria) => (
              <option value={categoria.id} key={categoria.id}>
                {categoria.nome}
              </option>
            ))}
          </Seletor>
          <Seletor rotulo="Origem" valor={origem} aoMudar={setOrigem}>
            <option value="">Todas</option>
            <option value={ORIGEM_ESCRITO_AQUI}>Escritos aqui</option>
            {Object.entries(ROTULO_DA_ORIGEM).map(([valor, rotulo]) => (
              <option value={valor} key={valor}>
                {rotulo}
              </option>
            ))}
          </Seletor>
          <Seletor rotulo="Módulo" valor={modulo} aoMudar={setModulo}>
            <option value="">Todos</option>
            <option value={SEM_MODULO}>Gerais (sem módulo)</option>
            {(modulos.data ?? []).map((item) => (
              <option value={item.chave} key={item.chave}>
                {item.nome}
              </option>
            ))}
          </Seletor>
        </div>
        <NovaCategoria />
      </Cartao>

      {artigos.isPending ? (
        <p role="status">Carregando artigos…</p>
      ) : artigos.isError ? (
        <Aviso tom="erro">{mensagemDoConsole(artigos.error)}</Aviso>
      ) : artigos.data.length === 0 ? (
        <Aviso tom="marca">Nenhum artigo com esses filtros.</Aviso>
      ) : (
        <Tabela legenda="Artigos da base de conhecimento, com estado, quem pode ler e avaliações.">
          <Cabecalho>
            <Linha>
              <CelulaCabecalho>Artigo</CelulaCabecalho>
              <CelulaCabecalho>Estado</CelulaCabecalho>
              <CelulaCabecalho>Quem lê</CelulaCabecalho>
              <CelulaCabecalho>Avaliações</CelulaCabecalho>
              <CelulaCabecalho>Atualizado</CelulaCabecalho>
            </Linha>
          </Cabecalho>
          <Corpo>
            {artigos.data.map((artigo) => (
              <Linha key={artigo.id} data-testid={`artigo-${artigo.id}`}>
                <Celula>
                  <Link to={`/admin/conhecimento/${artigo.id}`}>
                    <b>{artigo.titulo}</b>
                  </Link>
                  <div className="celula-secundaria">
                    {artigo.categoria ?? "Sem categoria"} · versão {artigo.versao_atual}
                    {artigo.modulo ? ` · ${artigo.modulo}` : ""}
                    {artigo.origem
                      ? ` · importado de ${ROTULO_DA_ORIGEM[artigo.origem] ?? artigo.origem}`
                      : ""}
                  </div>
                </Celula>
                <Celula>
                  <Chip estado={tomDoEstadoDoArtigo(artigo.estado)}>
                    {ROTULO_DO_ESTADO_DO_ARTIGO[artigo.estado]}
                  </Chip>
                </Celula>
                <Celula>
                  {ROTULO_DA_VISIBILIDADE[artigo.visibilidade]}
                  {artigo.organizacao ? (
                    <div className="celula-secundaria">{artigo.organizacao}</div>
                  ) : null}
                </Celula>
                <Celula>
                  {artigo.avaliacoes_uteis} úteis · {artigo.avaliacoes_nao_uteis} não úteis
                </Celula>
                <Celula>{dataHora(artigo.atualizado_em)}</Celula>
              </Linha>
            ))}
          </Corpo>
        </Tabela>
      )}
    </div>
  );
}
